import React, { useCallback, useState } from 'react';
import { func, arrayOf, string } from 'prop-types';
import { isEmpty } from 'underscore';
import classNames from 'classnames';

import { FormGroup, Label, Row, Col } from '@bingads-webui-react/primitive';
import { ValidationGroup, Input, TextArea } from '@bingads-webui-react/primitive-validation';
import { useI18n } from '@bingads-webui-campaign-react/smart-page-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { professionalServicePropTypes } from '../consts/prop-types';

const getStyles = () => ({
  formContainer: {
    '& .error': {
      color: '#a80000',
    },
  },
  description: {
    float: 'right',
    fontWeight: 'bold',
    position: 'relative',
  },
  descriptionContainer: {
    height: '120px',

    '& div[role=alert] + $description': {
      top: '-20px',
    },
  },
});

const nameMaxLength = 30;
const descriptionMaxLength = 200;

const serviceSchema = {
  name: 'Service schema',
  type: 'object',
  properties: {
    name: {
      type: 'string',
      required: true,
      maxLength: nameMaxLength,
    },
    price: {
      type: 'number',
      minimum: 0,
      maximum: 10000,
    },
    description: {
      type: 'string',
      maxLength: descriptionMaxLength,
    },
  },
};

export const ProfessionalServiceForm = ({
  onValidate,
  defaultValue,
  existingServiceNames,
}) => {
  const [description, setDescription] = useState(defaultValue.description || '');
  const i18n = useI18n();
  const classes = useDefaultStyles(getStyles);

  const nameValidator = useCallback((value) => {
    const validationValue = value.trim().toLowerCase();
    if (isEmpty(validationValue)) {
      return [i18n.getString(_TL_('Field is required'))];
    } else if (validationValue.length > nameMaxLength) {
      return [i18n.getString(_TL_('Field should not exceed {{maxCount}} characters'), { maxCount: nameMaxLength })];
    }
    return (existingServiceNames.indexOf(validationValue) > -1 ? [i18n.getString(_TL_('Name already existed'))] : []);
  }, [existingServiceNames, i18n]);

  const onDescriptionChange = useCallback(e => setDescription(e.target.value), []);

  return (
    <div className={classNames(classes.formContainer, 'signup-user-info-form')}>
      <ValidationGroup validationProps={{
        schema: serviceSchema,
        onValidate,
        data: defaultValue,
      }}
      >
        <FormGroup>
          <Row>
            <Col sm={12}> <Label id="serviceName">{i18n.getString(_TL_('Service name'))}</Label></Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Input
                validationProps={{ field: 'name', validator: nameValidator }}
                aria-labelledby="serviceName"
                className="service-name"
              />
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Row>
            <Col sm={12}><Label id="servicePrice">{i18n.getString(_TL_('Price (optional)'))}</Label></Col>
          </Row>
          <Row>
            <Col sm={12}>
              <Input
                validationProps={{ field: 'price' }}
                aria-labelledby="servicePrice"
                className="service-price"
              />
            </Col>
          </Row>
        </FormGroup>
        <FormGroup>
          <Row>
            <Col sm={12}>
              <Label>
                <div id="serviceDescription">{i18n.getString(_TL_('Short description (optional)'))}</div>
              </Label>
            </Col>
          </Row>
          <Row>
            <Col sm={12} className={classes.descriptionContainer}>
              <TextArea
                onChange={onDescriptionChange}
                validationProps={{ field: 'description' }}
                aria-labelledby="serviceDescription"
                className="service-description"
                rows={3}
                style={{ resize: 'none' }}
                placeholder={i18n.getString(_TL_('Write a short description about your business. It\'s best to include keywords in your description that people might use to search for your products or services.'))}
              />
              <span className={classes.description}>
                {
                  i18n.getString((_TL_('{{currentCount}}/{{maxCount}} Characters')), {
                    currentCount: description.length,
                    maxCount: descriptionMaxLength,
                  })
                }
              </span>
            </Col>
          </Row>
        </FormGroup>
      </ValidationGroup>
    </div>
  );
};

ProfessionalServiceForm.propTypes = {
  onValidate: func.isRequired,
  defaultValue: professionalServicePropTypes,
  existingServiceNames: arrayOf(string),
};

ProfessionalServiceForm.defaultProps = {
  existingServiceNames: [],
  defaultValue: {},
};
